import React, { useCallback, useState } from 'react';
import DynamicCrudPage from '../../../components/DynamicCrudPage';
import {
  useContracts,
  useCompanies,
  useFirms,
  useContractSubjects,
  useScopeOfWork,
  usePricingMethods,
  useWorkStatus,
  useContractsPermissions,
  SCREENS,
} from '../../../hooks';
import AddIcon from '@mui/icons-material/Add';
import { ROW_ACTIONS } from '../../../constants/tableConfigurations';
import ContractRenewalModal from '../../../components/ContractRenewalModal';

const ContractsPage = () => {
  const contractsPermissions = useContractsPermissions();
  const [renewModalOpen, setRenewModalOpen] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const customFormFields = useCallback((fields, additionalData, isRootUser) => {
    const {
      companies,
      firms,
      contractSubjects,
      scopeOfWork,
      pricingMethods,
      workStatus,
    } = additionalData;
    const companiesList = companies?.companies || [];
    const firmsList = firms?.referenceFirms || firms?.firms || [];
    const contractSubjectsList = contractSubjects?.contractSubjects || [];
    const scopeOfWorkList = scopeOfWork?.scopeOfWork || [];
    const pricingMethodsList = pricingMethods?.pricingMethods || [];
    const workStatusList = workStatus?.workStatus || [];

    return fields
      .map(field => {
        if (field.name === 'companyId') {
          return {
            ...field,
            options: companiesList.map(c => ({ value: c.id, label: c.name })),
            showIf: () => isRootUser,
          };
        }
        if (field.name === 'firmId') {
          return {
            ...field,
            options: firmsList.map(f => ({ value: f.id, label: f.firm_name })),
          };
        }
        if (field.name === 'firm_short_name') {
          return {
            ...field,
            type: 'select', // Explicitly set type
            options: firmsList.map(f => ({
              value: f.id,
              label: f.firm_short_name,
            })),
          };
        }
        if (field.name === 'contract_subject_id') {
          return {
            ...field,
            options: contractSubjectsList.map(cs => ({
              value: cs.id,
              label: cs.name,
            })),
          };
        }
        if (field.name === 'scope_of_work_id') {
          return {
            ...field,
            options: scopeOfWorkList.map(sw => ({
              value: sw.id,
              label: sw.name,
            })),
          };
        }
        if (field.name === 'pricing_method_id') {
          return {
            ...field,
            options: pricingMethodsList.map(pm => ({
              value: pm.id,
              label: pm.name,
            })),
          };
        }
        if (field.name === 'work_status_id') {
          return {
            ...field,
            options: workStatusList.map(ws => ({
              value: ws.id,
              label: ws.name,
            })),
          };
        }
        return field;
      })
      .filter(field => (field.name === 'companyId' ? isRootUser : true));
  }, []);

  const customFilterConfigs = useCallback((filters, data, isRootUser) => {
    return filters.filter(f => (f.showIfRoot ? isRootUser : true));
  }, []);

  const transformEditData = useCallback(contractData => {
    if (!contractData) return {};

    const formatDateForForm = dateString => {
      if (!dateString) return null;
      if (dateString instanceof Date) return dateString;
      const date = new Date(dateString);
      return isNaN(date.getTime()) ? null : date;
    };
    return {
      ...contractData,
      companyId: contractData.company_id,
      firmId: contractData.firm_id,
      firm_established:
        contractData.work_started_without_contract === true ? 'no' : 'yes',
      firm_short_name: contractData.firm_id,
      work_started_without_contract: contractData.work_started_without_contract === 'Geldi' ? 'yes' : 'no',
      auto_renewal: contractData.auto_renewal ? 'yes' : 'no',
      period_end_renewal_required: contractData.auto_renewal === false && contractData.renewal_period ? 'yes' : 'no',
      inflation_clause: contractData.inflation_clause ? 'yes' : 'no',
      // Tarih alanları - format düzeltmesi ile
      contract_signing_date: formatDateForForm(
        contractData.contract_signing_date
      ),
      work_start_date: formatDateForForm(contractData.work_start_date),
      invoice_issue_date: formatDateForForm(contractData.invoice_issue_date),
      protocol_start_date: formatDateForForm(contractData.protocol_start_date),
      protocol_end_date: formatDateForForm(contractData.protocol_end_date),
      renewal_period: contractData.renewal_period,
      first_price_increase_period: formatDateForForm(contractData.first_price_increase_period),
      comment: contractData.comment,
      contract_description: contractData.contract_description,
      // PDF alanları
      contract_pdf: contractData.contract_pdf_key
        ? {
            fileName: contractData.contract_pdf_key.split('/').pop(),
            key: contractData.contract_pdf_key,
            url: `https://ai-thinks-hr-docs.s3.eu-north-1.amazonaws.com/${contractData.contract_pdf_key}`,
            size: contractData.contract_pdf_size || 0,
            type: 'application/pdf',
          }
        : null,
      protocol_pdf: contractData.protocol_pdf_key
        ? {
            fileName: contractData.protocol_pdf_key.split('/').pop(),
            key: contractData.protocol_pdf_key,
            url: `https://ai-thinks-hr-docs.s3.eu-north-1.amazonaws.com/${contractData.protocol_pdf_key}`,
            size: contractData.protocol_pdf_size || 0,
            type: 'application/pdf',
          }
        : null,
    };
  }, []);

  const customRowActions = useCallback(
    (row) => ROW_ACTIONS.contracts(row, contractsPermissions),
    [contractsPermissions],
  );

  // 'renewContract' aksiyonu ContractRenewalModal'ı açar; diğer aksiyonlar
  // (edit/delete) için false dönerek DynamicCrudPage'in default handler'ına bırak.
  const handleRowAction = useCallback((actionType, row) => {
    if (actionType === 'renewContract') {
      setSelectedContract(row);
      setRenewModalOpen(true);
      return true;
    }
    return false;
  }, []);

  return (
    <>
      <DynamicCrudPage
        key={refreshKey}
        entityType="contracts"
        screen={SCREENS.CONTRACTS}
        commentEntityType="contract"
        hook={useContracts}
        // Client-pagination + sort: yenilemesi yakın/geçmiş sözleşmeleri üste
        // taşıyabilmek için tüm sözleşmeleri tek seferde yükleyip yerelde
        // sıralıyoruz (server'da renewal_priority hesabı yok).
        hookConfig={() => ({ paginated: false })}
        additionalHooks={{
          companies: useCompanies,
          firms: useFirms,
          contractSubjects: useContractSubjects,
          scopeOfWork: useScopeOfWork,
          pricingMethods: usePricingMethods,
          workStatus: useWorkStatus,
        }}
        addButtonTextKey="table.actions.addContract"
        addButtonIcon={<AddIcon />}
        entityTypeForDataTable="contract"
        customFormFields={customFormFields}
        customFilterConfigs={customFilterConfigs}
        customTransformEditData={transformEditData}
        customRowActions={customRowActions}
        onRowAction={handleRowAction}
        showAddButton={contractsPermissions.canCreate}
        // overdue → kırmızı (uyarı), due_soon → turuncu (dikkat).
        getRowSx={(row) => {
          if (row?.renewal_status === 'overdue') {
            return { backgroundColor: 'rgba(244, 67, 54, 0.10)' };
          }
          if (row?.renewal_status === 'due_soon') {
            return { backgroundColor: 'rgba(255, 152, 0, 0.10)' };
          }
          return {};
        }}
        // İlk yükleme sıralaması: yenileme önceliği yüksek olanlar üste.
        defaultSortConfig={{ field: 'renewal_priority', direction: 'desc' }}
      />
      <ContractRenewalModal
        open={renewModalOpen}
        contract={selectedContract}
        onClose={() => {
          setRenewModalOpen(false);
          setSelectedContract(null);
        }}
        onSuccess={() => setRefreshKey((k) => k + 1)}
      />
    </>
  );
};

export default ContractsPage;
